import React, { FC, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { BusinessInfoStep, BusinessInfoStepProps } from "components/Onboarding/components/V3/BusinessInfoStep";
import { CompetitiveInsights } from "components/Onboarding/components/CompetitiveInsights";
import {
    ProspectorPrefillNotice,
    ProspectorPrefillStatus,
} from "components/Onboarding/components/ProspectorPrefillNotice";
import { mapProspectorToOnboardingProps } from "components/Onboarding/utils/mapProspectorToOnboardingProps";
import { useProspectorBusinessQuery } from "generated/graphql";

import { OnboardingProps } from "../../models";

export const BusinessInfoStepContainer: FC<
    BusinessInfoStepProps & {
        initialProps: OnboardingProps;
        onNext(props: OnboardingProps): void;
        onBack?(): void;
    }
> = ({ aside, initialProps, ...props }) => {
    const { search } = useLocation();

    // The API requires exactly one of placeId or businessId; prefer placeId when both are present.
    const { placeId, businessId, variables } = useMemo(() => {
        const params = new URLSearchParams(search);
        const placeId = params.get("placeId") ?? undefined;
        const businessId = params.get("businessId") ?? undefined;
        return { placeId, businessId, variables: placeId ? { placeId } : { businessId } };
    }, [search]);

    const [prospectorResult] = useProspectorBusinessQuery({
        pause: !placeId && !businessId,
        variables,
        requestPolicy: "cache-first",
    });

    const prospectorBusiness = prospectorResult.data?.public?.business;

    // Surface the lookup so fields don't silently populate from a deep link.
    // A failed request ("error") is kept distinct from a genuine no-match ("not-found").
    const prefillStatus: ProspectorPrefillStatus = !(placeId || businessId)
        ? "idle"
        : prospectorResult.fetching
        ? "loading"
        : prospectorResult.error
        ? "error"
        : prospectorBusiness?.business
        ? "found"
        : "not-found";

    const mergedInitialProps = useMemo(
        () => mapProspectorToOnboardingProps(prospectorBusiness, initialProps ?? {}),
        [prospectorBusiness, initialProps]
    );

    // Only wrap the aside with competitive insights when we actually have prospector
    // data; the common organic-onboarding case has no placeId/businessId.
    const augmentedAside = prospectorBusiness ? (
        <>
            {aside}
            <CompetitiveInsights
                peers={prospectorBusiness.peers}
                competitors={prospectorBusiness.competitors}
                vertical={prospectorBusiness.business?.vertical}
                subjectBusinessId={prospectorBusiness.business?.businessId}
                subjectName={prospectorBusiness.business?.name}
            />
        </>
    ) : (
        aside
    );

    return (
        <BusinessInfoStep
            {...props}
            initialProps={mergedInitialProps}
            aside={augmentedAside}
            prefillLoading={prefillStatus === "loading"}
            prefillNotice={
                <ProspectorPrefillNotice status={prefillStatus} businessName={prospectorBusiness?.business?.name} />
            }
        />
    );
};
